import type * as C from "@zooi/contracts";
import { groupOf } from "./inbox-groups.ts";
import { type Metric, rangeEnd } from "./metric-dashboard.ts";

export type DashboardSearch = { add?: true; edit?: true; product?: string; weeks?: 4 | 12; grouped?: true };
export function dashboardSearch(search: Record<string, unknown>): DashboardSearch {
  return {
    ...(search.add === true || search.add === "true" ? { add: true as const } : {}),
    ...(search.grouped === true || search.grouped === "true" ? { grouped: true as const } : {}),
    ...(search.edit === true || search.edit === "true" ? { edit: true as const } : {}),
    ...(typeof search.product === "string" && search.product.length <= 100 ? { product: search.product } : {}),
    ...(search.weeks === 4 || search.weeks === "4" ? { weeks: 4 as const } : search.weeks === 12 || search.weeks === "12" ? { weeks: 12 as const } : {}),
  };
}

export function orderedProducts(data: C.WorkspaceDashboard, config = data.config) {
  return [...config.products.flatMap(entry => data.products.filter(row => row.product.id === entry.product_id)),
    ...data.products.filter(row => !config.products.some(entry => entry.product_id === row.product.id))];
}

export function summaryItems(items: C.InboxItem[], config: C.WorkspaceDashboardConfig["inbox"], product?: string) {
  const allowed = items.filter(item => (!product || item.product.id === product) && config.groups.includes(groupOf(item)));
  return allowed.sort((a, b) => {
    const group = config.groups.indexOf(groupOf(a)) - config.groups.indexOf(groupOf(b));
    return group || (a.due_at ? Date.parse(a.due_at) : Infinity) - (b.due_at ? Date.parse(b.due_at) : Infinity)
      || Date.parse(b.created_at) - Date.parse(a.created_at);
  });
}

export function move<T>(items: T[], index: number, offset: number): T[] {
  const next = items.slice(), target = index + offset;
  if (index < 0 || index >= items.length || target < 0 || target >= items.length) return next;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

type Row = C.WorkspaceDashboard["products"][number];
const activeMetrics = (row: Row): Metric[] => (row.all_metrics ?? row.metrics).filter(m => !m.presentation?.archived_at);

/**
 * What the whole view shares, said once above the blocks (docs/design-system.md
 * §0.4i, 2026-10-30): the product when only one is in view, the range from the
 * first period to the latest complete day, and the time zone the products share.
 */
export function dashboardContext(data: C.WorkspaceDashboard, product?: string): { product: Row | null; start: string | null; end: string | null; timeZone: string | null } {
  const rows = data.products.filter(row => !product || row.product.id === product);
  const starts = rows.flatMap(row => row.periods?.[0] ? [row.periods[0].start] : []).sort();
  const zones = new Set(rows.map(row => row.product.time_zone));
  return { product: rows.length === 1 ? rows[0] : null, start: starts[0] ?? null, end: rangeEnd(rows.flatMap(activeMetrics)), timeZone: zones.size === 1 ? [...zones][0] : null };
}

/** A product's metrics for a new block: its primary metric, then metrics with a number, then the rest; each says whether it is already shown. */
export function metricChoices(row: Row, blocks: C.DashboardBlock[]): { key: string; name: string; observed: boolean; shown: boolean }[] {
  const rank = (m: Metric) => (m.definition.key === row.product.primary_metric_key ? 0 : 2) + (m.range.value === null ? 1 : 0);
  return activeMetrics(row).map((m, i) => ({ m, i })).sort((a, b) => rank(a.m) - rank(b.m) || a.i - b.i).map(({ m }) => ({
    key: m.definition.key, name: m.presentation?.name ?? m.definition.name, observed: m.range.value !== null,
    shown: blocks.some(b => b.product_id === row.product.id && b.metric_key === m.definition.key),
  }));
}

/** The metric a new block starts on: the first one not shown yet, preferring one with a number. */
export function firstChoice(choices: ReturnType<typeof metricChoices>): string {
  return (choices.find(c => !c.shown && c.observed) ?? choices.find(c => !c.shown) ?? choices[0])?.key ?? "";
}
